<template>
  <b-row style="margin-top: 2em">
    <b-col>
      <b-card bg-variant="light">
        <b-row>
          <b-col>
            <h5 class="text-info">{{ $t("globals.items") }}:</h5>
          </b-col>
        </b-row>
        <!-- Items table -->
        <b-table
          small
          bordered
          :items="order.order_items"
          :fields="itemsFields"
        >
          <template v-slot:cell(item_name)="row">
            {{ row.item.item_name }}
            <b-btn
              v-if="
                row.item.price_per_month != 0 &&
                  order.status == 'completed' &&
                  getUserBrandPermissions.super_admin
              "
              class="float-right"
              size="sm"
              pill
              variant="light"
              @click="destroyItem(row.item.id)"
            >
              <v-icon name="trash"></v-icon>
            </b-btn>
          </template>
          <template v-slot:cell(price_one_time)="row">
            {{
              row.item.price_one_time === 0
                ? ""
                : currencyFormat(row.item.price_one_time, order.currency)
            }}
          </template>
          <template v-slot:cell(price_per_month)="row">
            {{
              row.item.price_per_month === 0
                ? ""
                : currencyFormat(row.item.price_per_month, order.currency)
            }}
            <b-btn
              v-if="
                row.item.price_per_month != 0 &&
                  order.status == 'completed' &&
                  getUserBrandPermissions.order_item_price_edit
              "
              class="float-right"
              size="sm"
              pill
              variant="light"
              @click="
                openOrderItemPriceModal(
                  row.item.id,
                  row.item.price_per_month,
                  row.item.item_name
                )
              "
            >
              <v-icon name="wrench"></v-icon>
            </b-btn>
          </template>
          <template v-slot:cell(quantity)="row">
            {{ row.item.quantity }}
            <b-btn
              v-if="
                order.status == 'completed' &&
                  getUserBrandPermissions.order_item_quantity_edit
              "
              class="float-right"
              size="sm"
              pill
              variant="light"
              @click="
                openOrderItemModal(
                  row.item.id,
                  row.item.quantity,
                  row.item.item_name
                )
              "
            >
              <v-icon name="wrench"></v-icon>
            </b-btn>
          </template>
          <template v-slot:cell(discount)="row">
            {{ currencyFormat(row.item.discount, order.currency) }}
          </template>
          <template v-slot:cell(vat)="row">
            {{ row.item.vat.toString() + "%" }}
          </template>
          <template v-slot:cell(vat_price)="row">
            {{
              currencyFormat(
                rowItemTotalVatOnly(
                  row.item.price_one_time,
                  row.item.price_per_month,
                  row.item.quantity,
                  order.contract_duration,
                  row.item.vat,
                  row.item.discount
                ),
                order.currency
              )
            }}
          </template>
          <template v-slot:cell(total_no_vat)="row">
            {{
              currencyFormat(
                rowItemTotalWithoutVat(
                  row.item.price_one_time,
                  row.item.price_per_month,
                  row.item.quantity,
                  row.item.discount,
                  order.contract_duration
                ),
                order.currency
              )
            }}
          </template>
          <template v-slot:cell(total)="row">
            {{
              currencyFormat(
                rowItemTotal(
                  row.item.price_one_time,
                  row.item.price_per_month,
                  row.item.quantity,
                  row.item.vat,
                  row.item.discount,
                  order.contract_duration
                ),
                order.currency
              )
            }}
          </template>
        </b-table>
        <b-row>
          <b-col>
            <b-btn
              v-if="
                order.status == 'completed' &&
                  getUserBrandPermissions.order_add_item
              "
              class="float-right m-1"
              size="sm"
              variant="info"
              @click="add_item_modal.show = true"
            >
              {{ $t("globals.add_item") }}
            </b-btn>
          </b-col>
        </b-row>
        <!-- Settings table -->
        <ulu-settings-table :settings="order.settings" />
        <!-- Notes -->
        <b-row>
          <b-col>
            <h5 class="text-info">{{ $t("globals.notes") }}:</h5>
          </b-col>
          <b-col>
            <b-btn
              v-if="
                order.status == 'completed' &&
                  getUserBrandPermissions.order_note_edit
              "
              class="float-right my-1"
              size="sm"
              variant="info"
              @click="order_notes_modal_show = true"
            >
              {{ $t("globals.edit") }}
            </b-btn>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <b-form-textarea v-model="order.notes" rows="3" disabled>
            </b-form-textarea>
          </b-col>
        </b-row>
      </b-card>
    </b-col>
    <ulu-order-item-quantity-modal
      :show="order_item_modal.show"
      :close="closeOrderItemModal"
      :order_item_id="order_item_modal.id"
      :order_item_quantity="order_item_modal.quantity"
      :order_item_name="order_item_modal.name"
    />
    <ulu-order-item-price-modal
      :show="order_item_price_modal.show"
      :close="closeOrderItemPriceModal"
      :order_item_id="order_item_price_modal.id"
      :order_item_price="order_item_price_modal.price"
      :order_item_name="order_item_price_modal.name"
    />
    <ulu-add-item-modal
      :show="add_item_modal.show"
      :close="closeAddItemModal"
      :currency="order.currency"
    />
    <ulu-order-notes-modal
      :show="order_notes_modal_show"
      :close="closeOrderNotesModal"
      :notes="order.notes"
    />
  </b-row>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import {
  rowItemTotalWithoutVat,
  rowItemTotal,
  currencyFormat,
  rowItemTotalVatOnly
} from "@/helpers/price";

export default {
  components: {
    "ulu-settings-table": () => import("@/components/common/SettingsTable"),
    "ulu-order-item-quantity-modal": () =>
      import(
        "@/components/orders/order/order_items_tab/OrderItemQuantityModal"
      ),
    "ulu-order-item-price-modal": () =>
      import("@/components/orders/order/order_items_tab/OrderItemPriceModal"),
    "ulu-order-notes-modal": () =>
      import("@/components/orders/order/order_items_tab/OrderNotesModal"),
    "ulu-add-item-modal": () =>
      import("@/components/orders/order/order_items_tab/AddItemModal")
  },
  props: {
    refresh: Function,
    order: {
      type: Object,
      default: () => {
        return {
          status: "",
          currency: "EUR",
          notes: "",
          settings: [],
          contract_duration: 0,
          items: []
        };
      }
    }
  },
  data() {
    return {
      order_notes_modal_show: false,
      order_item_modal: {
        show: false,
        id: null,
        quantity: null,
        name: null
      },
      order_item_price_modal: {
        show: false,
        id: null,
        price: null,
        name: null
      },
      add_item_modal: {
        show: false
      },
      itemsFields: [
        {
          key: "item_name",
          label: this.$t("globals.item_name")
        },
        {
          key: "price_one_time",
          label: this.$t("globals.price_once")
        },
        {
          key: "price_per_month",
          label: this.$t("globals.price_monthly")
        },
        {
          key: "quantity",
          label: this.$t("globals.quantity")
        },
        {
          key: "discount",
          label: this.$t("globals.discount")
        },
        {
          key: "vat",
          label: this.$t("globals.vat")
        },
        {
          key: "total_no_vat",
          label: this.$t("globals.value_no_vat"),
          class: "text-right"
        },
        {
          key: "vat_price",
          label: this.$t("globals.vat_price"),
          class: "text-right"
        },
        {
          key: "total",
          label: this.$t("globals.total"),
          class: "text-right"
        }
      ]
    };
  },
  methods: {
    ...mapActions("axios_mod", ["simpleMutation"]),
    currencyFormat,
    rowItemTotal,
    rowItemTotalWithoutVat,
    rowItemTotalVatOnly,
    openOrderNotesModal() {
      this.orde_notes_modal.show = true;
    },
    closeOrderNotesModal() {
      this.order_notes_modal_show = false;
      this.refresh();
    },
    openOrderItemModal(id, quantity, name) {
      this.order_item_modal.show = true;
      this.order_item_modal.id = id;
      this.order_item_modal.quantity = quantity;
      this.order_item_modal.name = name;
    },
    openOrderItemPriceModal(id, price, name) {
      this.order_item_price_modal.show = true;
      this.order_item_price_modal.id = id;
      this.order_item_price_modal.price = price;
      this.order_item_price_modal.name = name;
    },
    closeOrderItemModal() {
      this.order_item_modal.show = false;
      this.order_item_modal.id = null;
      this.order_item_modal.quantity = null;
      this.order_item_modal.name = null;
      this.refresh();
    },
    closeOrderItemPriceModal() {
      this.order_item_price_modal.show = false;
      this.order_item_price_modal.id = null;
      this.order_item_price_modal.price = null;
      this.order_item_price_modal.name = null;
      this.refresh();
    },
    closeAddItemModal() {
      this.add_item_modal.show = false;
      this.refresh();
    },
    destroyItem(item_id) {
      let data = {
        query: `
          mutation { 
            res: OrderItemDelete (
              order_item_id: ${item_id}
            )
          }
        `
      };

      this.simpleMutation({ data, this_ref: this }).then(() => {
        this.refresh();
      });
    }
  },
  computed: {
    ...mapGetters(["getUserBrandPermissions"])
  }
};
</script>
